import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import "./analytics.css";
import "./common-view.css";
import ClientSearch from "./client-search";
import useAuthSession from "../hooks/useAuthSession";
import { BACKEND_BASE_URL } from "../config/appUrls";
import { hasValidPlatformAccess } from "../utils/auth";
import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  Legend,
  CartesianGrid
} from "recharts";

export default function Analytics() {
  const { t, i18n } = useTranslation();
  const auth = useAuthSession();
  const { token, clientId, isAdmin, language } = auth;
  const [selectedClient, setSelectedClient] = useState(null);
  const [targetClientId, setTargetClientId] = useState(clientId || "");

  const [stats, setStats] = useState(null);
  const [activePanel, setActivePanel] = useState(null);
  const [range, setRange] = useState("7d");

  // Determine current active locale for date formatting
  const currentLocale = useMemo(() => {
    const lang = (i18n.language || language || "en").toLowerCase();
    return lang.startsWith("es") ? "es-ES" : "en-US";
  }, [i18n.language, language]);

  // Lock the function reference so it never changes across renders
  const handleClientSelect = useCallback(client => {
    setSelectedClient(client);
    setTargetClientId(client.client_id);
  }, []);

  const hasPlatformAccess = useMemo(() => {
    return hasValidPlatformAccess(selectedClient);
  }, [selectedClient]);

  useEffect(() => {
    if (!selectedClient) return;
    if (!hasPlatformAccess) {
      alert(t("dashboard.subscriptionRequiredDesc"));
      window.location.href = "/dashboard?tab=onboarding";
    }
  }, [hasPlatformAccess, selectedClient, t]);

  // Reload stats when range changes
  useEffect(() => {
    if (!token) return;
    if (!targetClientId) return;

    loadStats(targetClientId, range);
  }, [token, targetClientId, range]);

  async function loadStats(clientId, currentRange) {
    try {
      const res = await fetch(
        `${BACKEND_BASE_URL}/api/analytics?client_id=${clientId}&range=${currentRange}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "analytics failed");

      const calcDelta = (current, previous) => {
        if (!previous || previous === 0) return 0;
        return ((current - previous) / previous) * 100;
      };

      const enrichedStats = {
        ...data,
        revenueDelta: calcDelta(data.revenue, data.prev_revenue),
        ordersDelta: calcDelta(data.orders, data.prev_orders),
        aovDelta: calcDelta(data.aov_30_days, data.prev_aov),
        convDelta: calcDelta(
          parseFloat(data.conversion_rate),
          parseFloat(data.prev_conversion_rate)
        )
      };

      setStats(enrichedStats);
    } catch (e) {
      console.error(e);
      setStats(null);
    }
  }

  function formatDelta(delta) {
    if (!delta || isNaN(delta) || delta === 0) return "—";
    if (!isFinite(delta)) return "↑ ∞%";
    const sign = delta > 0 ? "↑" : "↓";
    return `${sign} ${Math.abs(delta).toFixed(1)}%`;
  }

  // Localized period label (falls back to backend period_label if key not present)
  const localizedPeriodLabel = useMemo(() => {
    return t(`analytics.ranges.${range}`, stats?.period_label || range);
  }, [t, range, stats?.period_label]);

  function SmartAreaChart({ data, series, type }) {
    const formatValue = value => {
      if (type === "currency") return Number(value).toFixed(0);
      if (type === "percent") return Number(value).toFixed(0);
      return Number(value);
    };

    return (
      <ResponsiveContainer width="100%" height={250}>
        {!data || data.length === 0 ? (
          <div style={{ textAlign: "center", padding: 20 }}>
            {t("analytics.charts.noData", "No data yet")}
          </div>
        ) : (
          <AreaChart
            data={data}
            margin={{ top: 10, right: 20, left: 30, bottom: 0 }}
          >
            <defs>
              {series.map((s, idx) => (
                <linearGradient
                  key={idx}
                  id={`color-${s.key}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor={s.color} stopOpacity={0.6} />
                  <stop offset="95%" stopColor={s.color} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>

            <XAxis
              dataKey="date"
              tickFormatter={d =>
                new Date(d).toLocaleDateString(currentLocale, {
                  month: "short",
                  day: "numeric"
                })
              }
            />

            <YAxis tickFormatter={formatValue} width={50} />

            <Tooltip
              formatter={(value, name) => {
                let formatted = Number(value);
                if (type === "currency") formatted = `$${Number(value).toFixed(2)}`;
                if (type === "percent") formatted = `${Number(value).toFixed(1)}%`;
                return [formatted, name];
              }}
              labelFormatter={label =>
                new Date(label).toLocaleDateString(currentLocale, {
                  weekday: "short",
                  month: "short",
                  day: "numeric"
                })
              }
            />

            <Legend verticalAlign="bottom" height={36} />

            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />

            {series.map((s, idx) => (
              <Area
                key={idx}
                type="monotone"
                dataKey={s.key}
                name={s.name || s.key}
                stroke={s.color}
                fill={`url(#color-${s.key})`}
                strokeWidth={2}
              />
            ))}
          </AreaChart>
        )}
      </ResponsiveContainer>
    );
  }

  return (
    <div className="view-page analytics">
      <div className="view-card">
        <>
          <div className="view-header">
            <div>
              <h2 className="view-title">{t("analytics.title")}</h2>
              <p className="view-subtitle">{t("analytics.subtitle")}</p>
            </div>

            <div className="view-nav">
              <select
                className="view-dropdown"
                defaultValue=""
                onChange={e => {
                  const route = e.target.value;
                  if (route) window.location.href = route;
                }}
              >
                <option value="" disabled>
                  {t("dropDown.analytics-page")}
                </option>
                <option value="/">{t("dropDown.home-page")}</option>
                <option value="/support">{t("dropDown.support-page")}</option>
                <option value="/qr-code">{t("dropDown.qr-page")}</option>
                <option value="/dashboard">
                  {t("dropDown.dashboard-page")}
                </option>
                <option value="/clients">{t("dropDown.clients-page")}</option>
                <option value="/scheduler">
                  {t("dropDown.scheduler-page")}
                </option>
                <option value="/commerce">{t("dropDown.commerce-page")}</option>
                <option value="/dispatcher">
                  {t("dropDown.dispatcher-page")}
                </option>
                <option value="/crm-lite">{t("dropDown.crm-lite-page")}</option>
                <option value="/tutorial">{t("dropDown.tutorial-page")}</option>
              </select>
            </div>
          </div>

          <ClientSearch onSelect={handleClientSelect} />

          {targetClientId && stats && (
            <div className="card">
              <div className="card-title">
                {t("analytics.selectedClient")}:{" "}
                {selectedClient
                  ? selectedClient.business_name || selectedClient.owner_name
                  : t("analytics.yourBusiness", t("analytics.myBusiness"))}
              </div>
              <div className="analytics-summary-grid">
                <div
                  className={`analytics-metric-card revenue clickable ${
                    activePanel === "revenue" ? "active" : ""
                  }`}
                  onClick={() => setActivePanel("revenue")}
                >
                  <div className="analytics-card-val">
                    💰 ${Number(stats.revenue || 0).toFixed(2)}
                  </div>
                  <div
                    className={`delta ${
                      stats.revenueDelta >= 0 ? "positive" : "negative"
                    }`}
                  >
                    {formatDelta(stats.revenueDelta)}
                  </div>
                  <span className="analytics-card-label">
                    {t("analytics.revenue")} · {localizedPeriodLabel}
                  </span>
                </div>

                <div
                  className={`analytics-metric-card orders clickable ${
                    activePanel === "orders" ? "active" : ""
                  }`}
                  onClick={() => setActivePanel("orders")}
                >
                  <div className="analytics-card-val">
                    📦 {stats.orders}
                  </div>
                  <div
                    className={`delta ${
                      stats.ordersDelta >= 0 ? "positive" : "negative"
                    }`}
                  >
                    {formatDelta(stats.ordersDelta)}
                  </div>
                  <span className="analytics-card-label">
                    {t("analytics.orders")} · {localizedPeriodLabel}
                  </span>
                </div>

                <div
                  className={`analytics-metric-card conversion clickable ${
                    activePanel === "conversion" ? "active" : ""
                  }`}
                  onClick={() => setActivePanel("conversion")}
                >
                  <div className="analytics-card-val">
                    🔥 {stats.conversion_rate || "0"}%
                  </div>
                  <div
                    className={`delta ${
                      stats.convDelta >= 0 ? "positive" : "negative"
                    }`}
                  >
                    {formatDelta(stats.convDelta)}
                  </div>
                  <span className="analytics-card-label">
                    {t("analytics.conversion", "Conversión")} · {localizedPeriodLabel}
                  </span>
                </div>

                <div
                  className={`analytics-metric-card avg clickable ${
                    activePanel === "aov" ? "active" : ""
                  }`}
                  onClick={() => setActivePanel("aov")}
                >
                  <div className="analytics-card-val">
                    🧾 ${Number(stats.aov_30_days || 0).toFixed(2)}
                  </div>
                  <div
                    className={`delta ${
                      stats.aovDelta >= 0 ? "positive" : "negative"
                    }`}
                  >
                    {formatDelta(stats.aovDelta)}
                  </div>
                  <span className="analytics-card-label">
                    {t("analytics.aov")} · {localizedPeriodLabel}
                  </span>
                </div>
              </div>

              {activePanel === "revenue" && (
                <>
                  <div className="chart-header">
                    <div className="chart-title">
                      {t("analytics.charts.revenue", "Revenue ($)")}
                    </div>

                    <div className="chart-controls">
                      {["1d", "7d", "30d", "1y"].map(r => (
                        <button
                          key={r}
                          className={range === r ? "active" : ""}
                          onClick={() => setRange(r)}
                        >
                          {r}
                        </button>
                      ))}

                      <button
                        className="close-btn"
                        onClick={() => setActivePanel(null)}
                      >
                        ✕
                      </button>
                    </div>
                  </div>

                  <div className="analytics-chart">
                    <SmartAreaChart
                      data={stats.revenue_series}
                      type="currency"
                      series={[
                        {
                          key: "revenue",
                          name: t("analytics.revenue", "Revenue"),
                          color: "#22c55e"
                        }
                      ]}
                    />
                  </div>
                </>
              )}

              {activePanel === "orders" && (
                <>
                  <div className="chart-header">
                    <div className="chart-title">
                      {t("analytics.charts.orders", "Orders")}
                    </div>

                    <div className="chart-controls">
                      {["1d", "7d", "30d", "1y"].map(r => (
                        <button
                          key={r}
                          className={range === r ? "active" : ""}
                          onClick={() => setRange(r)}
                        >
                          {r}
                        </button>
                      ))}

                      <button
                        className="close-btn"
                        onClick={() => setActivePanel(null)}
                      >
                        ✕
                      </button>
                    </div>
                  </div>

                  <div className="analytics-chart">
                    <SmartAreaChart
                      data={stats.orders_series}
                      type="count"
                      series={[
                        {
                          key: "orders",
                          name: t("analytics.orders", "Orders"),
                          color: "#3b82f6"
                        }
                      ]}
                    />
                  </div>
                </>
              )}

              {activePanel === "conversion" && (
                <>
                  <div className="chart-header">
                    <div className="chart-title">
                      {t("analytics.charts.conversionFunnel", "Conversion Funnel")}
                    </div>

                    <div className="chart-controls">
                      {["1d", "7d", "30d", "1y"].map(r => (
                        <button
                          key={r}
                          className={range === r ? "active" : ""}
                          onClick={() => setRange(r)}
                        >
                          {r}
                        </button>
                      ))}

                      <button
                        className="close-btn"
                        onClick={() => setActivePanel(null)}
                      >
                        ✕
                      </button>
                    </div>
                  </div>

                  <div className="analytics-chart">
                    <SmartAreaChart
                      data={stats.conversion_series}
                      type="count"
                      series={[
                        {
                          key: "leads",
                          name: t("analytics.funnel.leads", "Leads"),
                          color: "#9ca3af"
                        },
                        {
                          key: "contacted",
                          name: t("analytics.funnel.contacted", "Contacted"),
                          color: "#3b82f6"
                        },
                        {
                          key: "qualified",
                          name: t("analytics.funnel.qualified", "Qualified"),
                          color: "#f97316"
                        },
                        {
                          key: "converted",
                          name: t("analytics.funnel.converted", "Converted"),
                          color: "#16a34a"
                        }
                      ]}
                    />
                  </div>
                </>
              )}

              {activePanel === "aov" && (
                <>
                  <div className="chart-header">
                    <div className="chart-title">
                      {t("analytics.charts.aov", "Average Order Value ($)")}
                    </div>

                    <div className="chart-controls">
                      {["1d", "7d", "30d", "1y"].map(r => (
                        <button
                          key={r}
                          className={range === r ? "active" : ""}
                          onClick={() => setRange(r)}
                        >
                          {r}
                        </button>
                      ))}

                      <button
                        className="close-btn"
                        onClick={() => setActivePanel(null)}
                      >
                        ✕
                      </button>
                    </div>
                  </div>

                  <div className="analytics-chart">
                    <SmartAreaChart
                      data={stats.aov_series}
                      type="currency"
                      series={[
                        {
                          key: "aov",
                          name: t("analytics.aov", "Average Order Value"),
                          color: "#8b5cf6"
                        }
                      ]}
                    />
                  </div>
                </>
              )}

              <div className="insight highlight">
                <div className="card-title">
                  💡 {t("analytics.insights.title", "Smart Insights")}
                </div>

                <div className="insight success">
                  📈 {t("analytics.insights.bestDay", "Best sales day:")}
                  <strong>
                    {" "}
                    {stats.best_day
                      ? new Date(stats.best_day).toLocaleDateString(currentLocale)
                      : "—"}
                  </strong>
                </div>

                <div className="insight warning">
                  ⏰ {t("analytics.insights.peakHour", "Peak engagement hour:")}
                  <strong> {stats.peak_hour}:00</strong>
                </div>

                <div className="insight info">
                  💬 {t("analytics.insights.topQuestion", "Top customer question:")}
                  <em>
                    {" "}
                    "
                    {stats.top_question === "Coming soon..."
                      ? t("analytics.insights.comingSoon", "Coming soon...")
                      : stats.top_question || "—"}
                    "
                  </em>
                </div>

                <div className="insight action">
                  🚀 {t("analytics.insights.recPrefix", "Recommendation: Promote your business around")}{" "}
                  <strong>{stats.peak_hour}:00</strong>{" "}
                  {t("analytics.insights.recSuffix", "to increase conversions.")}
                </div>
              </div>
            </div>
          )}
        </>
      </div>
    </div>
  );
}